import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { usePerformanceReviewsHook } from "@/hooks/use-performance-reviews.hook.tsx";
import { useSearchMyFeedbacks } from "@/queries/employees/use-employee-performance-review.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiFormParticipantStatus } from "@/types/forms/form.types.ts";
import {
  ApiPerformanceReviewParticipant,
  ApiPerformanceReviewParticipantFilter,
  ApiPerformanceReviewReviewerTypes,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import {
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  Page,
  PaginationBlock,
  Stack,
  Table,
  TableCell,
  TextTruncated,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { FormResultForReviewerModal } from "@/pages/talent/performance-reviews/view/widgets/FormResultForReviewerModal.tsx";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "react-use";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export const MyFeedbacksLeft = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModals();
  const { renderDate, renderTimeAgo } = useFormatter();
  const { getPerformanceReviewStatusTag } = usePerformanceReviewsHook();
  const library = filtersLibrary();
  const [, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const {
    filter: { filter: filter },
  } = useAppFilter<ApiPerformanceReviewParticipantFilter>({
    filters: [library.page_number, library.text],
    source: "My feedbacks left",
  });

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set(library.text.searchParamName, searchTerm);
        else searchParams.delete(library.text.searchParamName);
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const {
    data: participantData,
    isLoading: isLoading,
    isFetching: isFetching,
  } = useSearchMyFeedbacks(filter);

  const columns = useMemo<ColumnDef<ApiPerformanceReviewParticipant>[]>(
    () => [
      {
        id: "employees",
        header: t("core:full_name"),
        cell: (cell) => (
          <EmployeeHoverCard
            employee={cell.row.original.employee}
            position={getEmployeePositionTitle(cell.row.original.employee)}
          >
            <EmployeeItem showPosition={false} employee={cell.row.original.employee} />
          </EmployeeHoverCard>
        ),
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
      },
      {
        id: "date",
        header: t("common:date"),
        cell: (cell) => {
          const reviewerType = cell.row.original.reviewer_types.find(
            (rt) => rt.datetime_completed_at
          );
          if (!reviewerType?.datetime_completed_at) return "-";
          const date = new Date(reviewerType.datetime_completed_at);
          return (
            <Tooltip>
              <TooltipTrigger>
                <TableCell variant="secondary">{renderTimeAgo(date)}</TableCell>
              </TooltipTrigger>
              <TooltipContent>{renderDate(date)}</TooltipContent>
            </Tooltip>
          );
        },
        ...ColumnSizes.middle,
      },
      {
        id: "type",
        header: t("common:type"),
        cell: (cell) => {
          const reviewerType = cell.row.original.reviewer_types.find(
            (rt) => rt.datetime_completed_at
          );
          if (!reviewerType) return "-";

          const typeItem = PerformanceReviewReviewerTypeItems().find(
            (item) => item.id === reviewerType.type
          );
          const label =
            reviewerType.type === ApiPerformanceReviewReviewerTypes.custom
              ? reviewerType.title
              : typeItem?.shortTitle;

          return <TableCell variant={"secondary"}>{label || "-"}</TableCell>;
        },
        ...ColumnSizes.middle,
      },
      {
        header: t("performance_review:cycle_status"),
        cell: (row) => (
          <Stack items={"start"}>
            {getPerformanceReviewStatusTag(row.row.original.review.status)}
          </Stack>
        ),
        accessorKey: "status",
        ...ColumnSizes.middle,
      },
      {
        id: "performance_review",
        header: t("performance_review:review_cycle"),
        cell: (cell) => (
          <TextTruncated title={cell.row.original.review.title} className={"text-secondary"} />
        ),
        ...ColumnSizes.wide,
      },
    ],
    [openModal, renderDate, t]
  );

  const getEditAnswers = useCallback(
    (item: ApiPerformanceReviewParticipant) => {
      const reviewerType = item.reviewer_types.find((rt) => rt.datetime_completed_at);
      const reviewerId = reviewerType?.reviewer_id;
      if (!reviewerType?.can_edit_results || !reviewerId) return undefined;
      return () =>
        navigate(`/performance-reviews/reviewers/${reviewerId}/respond`, {
          state: {
            forcedReviewerStatus: ApiFormParticipantStatus.in_progress,
            from: location.pathname,
          },
        });
    },
    [navigate, location.pathname]
  );

  const openResults = useCallback(
    (item: ApiPerformanceReviewParticipant) => {
      const reviewerType = item.reviewer_types.find((rt) => rt.datetime_completed_at);
      if (!reviewerType?.respondent_uuid) return;
      openModal(FormResultForReviewerModal, {
        reviewerTypeId: reviewerType.id,
        respondentUuid: reviewerType.respondent_uuid,
        performanceReviewId: item.review.id,
        employeeId: item.employee.id,
        onEditAnswers: getEditAnswers(item),
      });
    },
    [openModal, getEditAnswers]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiPerformanceReviewParticipant>) => {
      const item = row.original;
      const reviewerType = item.reviewer_types.find((rt) => rt.datetime_completed_at);
      if (!reviewerType?.respondent_uuid) return null;
      const editAnswers = getEditAnswers(item);

      return (
        <DropdownMenu>
          <DropdownMenuItem onClick={() => openResults(item)} title={t("common:view")} />
          {editAnswers && (
            <DropdownMenuItem onClick={editAnswers} title={t("scorecards:change_answers")} />
          )}
        </DropdownMenu>
      );
    },
    [openResults, getEditAnswers, t]
  );

  return (
    <Page.StickyLayout
      toolbar={
        <Search
          value={searchTerm}
          onInput={(e) => setSearchTerm(e.currentTarget.value)}
          placeholder={t("common:search")}
        />
      }
      footer={
        <PaginationBlock
          metadata={participantData?.metadata}
          onPageChange={(page) => {
            setSearchParams((searchParams) => {
              searchParams.set("page", page.toString());
              return searchParams;
            });
          }}
        />
      }
    >
      <Table
        stickyHeader={true}
        renderRowAction={renderRowAction}
        onRowClick={(row) => openResults(row.original)}
        data={participantData?.items || []}
        columns={columns}
        showSkeleton={isLoading}
        isFetching={isLoading || isFetching}
        showNotFoundError={filter.text != undefined && participantData?.metadata.total_items == 0}
        notFoundComponent={<EmptyStateNothingFound />}
        emptyStateComponent={
          <EmptyStateBase
            emoji={emojiDefaults.default}
            title={t("performance_review:feedbacks_left_empty_title")}
            description={t("performance_review:feedbacks_left_empty_description")}
          />
        }
      />
    </Page.StickyLayout>
  );
};
